import { Link } from 'react-router-dom';

import { Icon } from '~/components';
import { cn } from '~/lib/cn';

import type { ColDef } from '../../components/DataGrid/types';
import type { DashboardRow, TrackMark } from '../../types/dashoard';
import { listFormatter, zeroFormatter } from './renderers';

// type Ephimeral = keyof DashboardRow;

export const columnDefs: ColDef[] = [
  {
    field: 'isrc',
    headerName: 'ISRC',
    width: 155,
    cellClass: 'text-white font-semibold text-body-xs',
    sortable: true,
    cellRenderer: (p) => {
      const lock = p.data.trackLock as TrackMark;
      const ignore = p.data.trackIgnore as TrackMark;

      return (
        <div
          data-testid="isrc-container"
          className={cn(
            'flex items-center',
            ignore && 'line-through text-subtle',
          )}
        >
          <span
            data-testid="track-ignore"
            title={
              ignore
                ? `Disabled by: ${(ignore as { owner: { name: string }; createdAt: Date }).owner.name} 
                   on ${(ignore as { owner: { name: string }; createdAt: Date }).createdAt.toLocaleDateString()}`
                : undefined
            }
          >
            <Link
              to={`../territories?id=${p.data.isrc}`}
              data-testid="isrc-link"
            >
              {p.data.isrc as string}
            </Link>
          </span>
          {lock ? (
            <Icon
              className={cn(
                'ml-sm text-body-xs',
                (lock as { self?: boolean }).self
                  ? 'text-subtle'
                  : 'text-error-alert',
              )}
              data-testid="track-lock-icon"
              type="eye"
              variant="solid"
              title={(() => {
                const lockData = lock as {
                  owner?: { name: string };
                  createdAt?: Date;
                };
                return lockData.owner && lockData.createdAt
                  ? `Locked by: ${lockData.owner.name} on ${lockData.createdAt.toLocaleDateString()}`
                  : undefined;
              })()}
            />
          ) : null}
        </div>
      );
    },
  },
  // {
  //   field: '_rollupTitle' as Ephimeral,
  //   headerName: 'Rollup Title',
  //   flex: 1,
  //   cellClass: 'text-white font-semibold text-body-xs',
  //   cellRenderer: (p) => {
  //     if (!p?.data) {
  //       return null;
  //     }

  //     const data = p.data as Record<string, unknown>;
  //     const trackTitle = data.trackTitle as string;
  //     const artist = data.artist as string;

  //     return (
  //       <div>
  //         <span>{trackTitle}</span>
  //         <span className="font-extralight text-default"> by... </span>
  //         <span className="font-light">{artist}</span>
  //       </div>
  //     );
  //   },
  //   colDependsOn: ['trackTitle', 'artist'],
  //   sortable: true,
  // },
  {
    field: 'artist',
    headerName: 'Artist',
    minWidth: 150,
    flex: 1,
    sortable: true,
    cellRenderer: (p) => {
      if (!p?.data) {
        return null;
      }
      const { artist, overwrites } = p.data as Partial<DashboardRow>;
      return (
        <div>
          {overwrites?.artist ? (
            <Icon
              className="mr-xs text-body-xs text-subtle"
              type="marker"
              title={overwrites?.artist}
            />
          ) : null}
          <span>{artist}</span>
        </div>
      );
    },
  },
  {
    field: 'trackTitle',
    headerName: 'Track Title',
    flex: 1,
    sortable: true,
    minWidth: 200,
    cellRenderer: (p) => {
      if (!p?.data) {
        return null;
      }
      const { trackTitle, overwrites } = p.data as Partial<DashboardRow>;
      return (
        <div>
          {overwrites?.trackTitle ? (
            <Icon
              className="mr-xs text-body-xs text-subtle"
              type="marker"
              title={overwrites?.trackTitle}
            />
          ) : null}
          <span>{trackTitle}</span>
        </div>
      );
    },
  },
  // {
  //   field: 'shortTitle',
  //   headerName: 'Short Title',
  //   width: 120,
  //   sortable: true,
  //   hide: true,
  // },
  // {
  //   field: 'recordingVersion',
  //   headerName: 'Recording Version',
  //   width: 120,
  //   sortable: true,
  //   hide: true,
  // },
  {
    field: 'recType',
    headerName: 'Recording Type',
    minWidth: 140,
    flex: 1,
    sortable: true,
  },
  {
    field: 'recSubType',
    headerName: 'Recording Sub Type',
    minWidth: 160,
    flex: 1,
    sortable: true,
  },
  {
    field: 'audioPresentation',
    headerName: 'Audio Presentation',
    minWidth: 120,
    flex: 1,
    sortable: true,
    hide: true,
  },
  {
    field: 'gpid',
    headerName: 'GPID(s)',
    filterName: 'GPID',
    cellClass: 'text-white font-semibold text-body-xs',
    minWidth: 150,
    flex: 1,
    cellRenderer: (p) => {
      if (!p?.data?.gpid) {
        return null;
      }

      const gpids = (p.data as { gpid?: string[] }).gpid ?? [];
      if (gpids.length === 0) {
        return null;
      }

      return (
        <div data-testid="gpid-container" className="flex">
          {gpids.length > 0 && <span data-testid="gpid-value">{gpids[0]}</span>}
          {gpids.length > 1 && (
            <span data-testid="gpid-more" className="ml-sm">
              (+{gpids.length - 1})
            </span>
          )}
        </div>
      );
    },
    sortable: true,
  },
  // {
  //   field: 'albumTitle',
  //   headerName: 'Album Title',
  //   flex: 1,
  //   valueFormatter: listFormatter,
  //   hide: true,
  //   sortable: true,
  // },
  // {
  //   field: 'albumSubtitle',
  //   headerName: 'Album Subtitle',
  //   flex: 1,
  //   valueFormatter: listFormatter,
  //   hide: true,
  //   sortable: true,
  // },
  {
    field: 'majorLabel',
    headerName: 'Major Label',
    flex: 1,
    cellClass: 'text-white font-semibold text-body-xs',
    valueFormatter: listFormatter,
    sortable: true,
    minWidth: 180,
  },
  {
    field: 'presentationLabel',
    headerName: 'Presentation Label',
    cellClass: 'text-white font-semibold text-body-xs',
    valueFormatter: listFormatter,
    flex: 1,
    sortable: true,
    minWidth: 180,
  },
  {
    field: 'repertoireOwner',
    headerName: 'Repertoire Owner',
    flex: 1,
    sortable: true,
    hide: true,
  },
  {
    field: 'releaseType',
    headerName: 'Release Type(s)',
    filterName: 'Release Type',
    width: 140,
    cellClass: 'text-white font-semibold text-body-xs',
    valueFormatter: listFormatter,
    sortable: true,
  },
  {
    field: 'paFlag',
    headerName: 'PA Flag',
    width: 120,
    sortable: true,
    hide: true,
  },
  {
    field: 'explicit',
    headerName: 'Explicit',
    flex: 1,
    sortable: true,
    hide: true,
  },
  // {
  //   field: 'trackRightType',
  //   headerName: 'Track Right Type',
  //   flex: 1,
  //   sortable: true,
  //   hide: true,
  // },
  // {
  //   field: '_optTerritory' as Ephimeral,
  //   headerName: 'KPI: Territory Delivery',
  //   width: 140,
  //   resizable: false,
  //   cellRenderer: OptTerrRenderer(
  //     'deliveryTerritoryCount',
  //     'missedTerritoriesCountAlt',
  //   ),
  //   cellClass: 'text-white font-semibold text-body-xs',
  //   colDependsOn: ['deliveryTerritoryCount', 'missedTerritoriesCountAlt'],
  //   sortable: true,
  // },
  // {
  //   field: 'projectedMissedStreams',
  //   headerName: 'KPI: Missed Streams / Year',
  //   width: 120,
  //   sortable: true,
  //   hide: true,
  //   valueFormatter: zeroFormatter,
  //   valueGetter: (p) => {
  //     return parseInt(
  //       String(
  //         (p.data as { projectedMissedStreams?: number })
  //           .projectedMissedStreams ?? '0',
  //       ),
  //       10,
  //     );
  //   },
  // },
  // {
  //   field: '_appleTerritoryCount' as Ephimeral,
  //   headerName: 'KPI: Apple Delivery',
  //   width: 140,
  //   cellRenderer: OptTerrRenderer(
  //     'appleTerritoryCount',
  //     'missedAppleTerritoriesCountAlt',
  //   ),
  //   colDependsOn: ['appleTerritoryCount', 'missedAppleTerritoriesCountAlt'],
  //   sortable: true,
  //   hide: true,
  // },
  // {
  //   field: '_spotifyTerritoryCount' as Ephimeral,
  //   headerName: 'KPI: Spotify Delivery',
  //   width: 140,
  //   cellRenderer: OptTerrRenderer(
  //     'spotifyTerritoryCount',
  //     'missedSpotifyTerritoriesCountAlt',
  //   ),
  //   colDependsOn: ['spotifyTerritoryCount', 'missedSpotifyTerritoriesCountAlt'],
  //   sortable: true,
  //   hide: true,
  // },
  // {
  //   field: '_streamingTerritoryCount' as Ephimeral,
  //   headerName: 'KPI: Streaming Terr.',
  //   width: 140,
  //   cellRenderer: OptTerrRenderer(
  //     'streamingTerritoryCount',
  //     'misstedStreamingTerritoryCount',
  //   ),
  //   colDependsOn: ['streamingTerritoryCount', 'misstedStreamingTerritoryCount'],
  //   sortable: true,
  //   hide: true,
  // },
  // {
  //   field: 'monthlyTrendRevenue',
  //   headerName: 'KPI: Revenue Monthly Trend',
  //   width: 120,
  //   sortable: true,
  //   hide: true,
  //   valueFormatter: zeroFormatter,
  // },
  // {
  //   field: 'monthlyTrendStreams',
  //   headerName: 'KPI: Streams Monthly Trend',
  //   width: 120,
  //   sortable: true,
  //   hide: true,
  //   valueFormatter: zeroFormatter,
  // },
  // List / Comparison
  {
    field: 'trackRightsTerritories',
    headerName: 'Track Rights Terr.',
    flex: 1,
    valueFormatter: listFormatter,
    hide: true,
  },
  {
    field: 'deliveryTerritoryCount',
    headerName: '# Delivery Terr.',
    width: 140,
    sortable: true,
  },
  {
    field: 'deliveryTerritoryList',
    headerName: 'Delivery Terr.',
    flex: 1,
    minWidth: 140,
    valueFormatter: listFormatter,
  },
  {
    field: 'missedTerritoriesCountAlt',
    headerName: '# Missed Terr.',
    width: 120,
    sortable: true,
  },
  {
    field: 'missedTerritoriesAlt',
    headerName: 'Missed Terr.',
    flex: 1,
    minWidth: 140,
    valueFormatter: listFormatter,
  },
  {
    field: 'trackRightsTerritoryCount',
    headerName: '# Track Rights Terr.',
    width: 140,
    sortable: true,
    hide: true,
  },
  {
    field: 'partnerCount',
    headerName: '# Partners',
    minWidth: 100,
    sortable: true,
    flex: 1,
  },
  {
    field: 'partnerList',
    headerName: 'Partners',
    flex: 1,
    valueFormatter: listFormatter,
    minWidth: 140,
  },
  {
    field: 'missedAppleTerritoriesCountAlt',
    headerName: 'Apple: # Missed Terr.',
    minWidth: 180,
    flex: 1,
    sortable: true,
  },
  {
    field: 'missedAppleTerritoriesAlt',
    headerName: 'Apple: Missed Terr.',
    flex: 1,
    minWidth: 140,
    valueFormatter: listFormatter,
  },
  {
    field: 'appleTerritoryCount',
    headerName: 'Apple: # Terr.',
    minWidth: 140,
    flex: 1,
    sortable: true,
  },
  {
    field: 'missedSpotifyTerritoriesCountAlt',
    headerName: 'Spotify: # Missed Terr.',
    minWidth: 140,
    flex: 1,
    sortable: true,
  },
  {
    field: 'missedSpotifyTerritoriesAlt',
    headerName: 'Spotify: Missed Terr.',
    flex: 1,
    valueFormatter: listFormatter,
    minWidth: 150,
  },
  {
    field: 'spotifyTerritoryCount',
    headerName: 'Spotify: # Terr.',
    minWidth: 140,
    flex: 1,
    sortable: true,
  },
  // {
  //   field: 'streamingTerritoryCount',
  //   headerName: 'Streaming: # Terr.',
  //   width: 140,
  //   sortable: true,
  //   hide: true,
  // },
  // {
  //   field: 'streamingTerritoryList',
  //   headerName: 'Streaming: Territories',
  //   flex: 1,
  //   valueFormatter: listFormatter,
  //   hide: true,
  // },
  // {
  //   field: 'misstedStreamingTerritoryCount',
  //   headerName: 'Streaming: # Missed Terr.',
  //   width: 120,
  //   sortable: true,
  //   hide: true,
  // },
  // {
  //   field: 'missedStreamingTerritories',
  //   headerName: 'Streaming: Missed Terr.',
  //   flex: 1,
  //   valueFormatter: listFormatter,
  //   hide: true,
  // },
  // Streaming statistic
  // {
  //   field: 'dailyStreamsAvg',
  //   headerName: 'Daily Stream Avg.',
  //   width: 120,
  //   sortable: true,
  //   hide: true,
  //   valueFormatter: zeroFormatter,
  // },
  // {
  //   field: 'dailyStreamsAvgLw',
  //   headerName: 'Daily Streaming Avg., Last Week',
  //   width: 120,
  //   sortable: true,
  //   hide: true,
  //   valueFormatter: zeroFormatter,
  // },
  // {
  //   field: 'wtdStreams',
  //   headerName: 'Week to Day Streams',
  //   width: 120,
  //   sortable: true,
  //   hide: true,
  //   valueFormatter: zeroFormatter,
  // },
  {
    field: 'lastCompleteWeekStreams',
    headerName: 'Last Complete Week Streams',
    width: 120,
    sortable: true,
    hide: true,
    valueFormatter: zeroFormatter,
  },
  // {
  //   field: 'mtdStreams',
  //   headerName: 'Month to Date Streams',
  //   width: 120,
  //   sortable: true,
  //   hide: true,
  //   valueFormatter: zeroFormatter,
  // },
  // {
  //   field: 'momPctStreams',
  //   headerName: 'Month over Month % Streams',
  //   width: 120,
  //   sortable: true,
  //   hide: true,
  //   valueFormatter: zeroFormatter,
  // },
  // {
  //   field: 'ytdStreams',
  //   headerName: 'Year to Day Streams',
  //   width: 120,
  //   sortable: true,
  //   hide: true,
  //   valueFormatter: zeroFormatter,
  // },
  // Revenue
  // {
  //   field: 'momPctRevenue',
  //   headerName: 'Month over Month % Revenue Change',
  //   width: 120,
  //   sortable: true,
  //   hide: true,
  //   valueFormatter: zeroFormatter,
  // },
  {
    field: 'momPctRevenueNet',
    headerName: 'Month over Month % Revenue Change (Net)',
    width: 120,
    sortable: true,
    hide: true,
    valueFormatter: zeroFormatter,
  },
  // {
  //   field: 'lmrGrossValue',
  //   headerName: 'Last Month Gross Revenue',
  //   width: 120,
  //   sortable: true,
  //   hide: true,
  //   valueFormatter: zeroFormatter,
  // },
  {
    field: 'lmrNetValue',
    headerName: 'Last Month Revenue (Net)',
    width: 120,
    sortable: true,
    hide: true,
    valueFormatter: zeroFormatter,
  },
  // {
  //   field: 'llmrGrossValue',
  //   headerName: 'Last to Last Month Gross Revenue',
  //   width: 120,
  //   sortable: true,
  //   hide: true,
  //   valueFormatter: zeroFormatter,
  // },
  {
    field: 'llmrNetValue',
    headerName: 'Last to Last Month Revenue (Net)',
    width: 120,
    sortable: true,
    hide: true,
    valueFormatter: zeroFormatter,
  },
  // {
  //   field: 'revenueYtdUsd',
  //   headerName: 'Revenue Year to Date, USD',
  //   width: 120,
  //   sortable: true,
  //   hide: true,
  //   valueFormatter: zeroFormatter,
  // },
  {
    field: 'monthlyTrendFixesAddedRevenue',
    headerName: 'Fixes: Added Revenue',
    width: 120,
    sortable: true,
    hide: true,
    valueFormatter: zeroFormatter,
  },
  // {
  //   field: 'monthlyTrendFixesRemovedRevenue',
  //   headerName: 'Fixes: TKD Revenue Miss',
  //   width: 120,
  //   sortable: true,
  //   hide: true,
  //   valueFormatter: zeroFormatter,
  // },
  {
    field: 'monthlyTrendRevenueNet',
    headerName: 'Monthly Revenue Trend (Net)',
    width: 120,
    sortable: true,
    hide: true,
    valueFormatter: zeroFormatter,
  },
  {
    field: 'monthlyTrendFixesAddedStreams',
    headerName: 'Fixes: Added Streams',
    width: 120,
    sortable: true,
    hide: true,
    valueFormatter: zeroFormatter,
  },
  // {
  //   field: 'monthlyTrendFixesRemovedStreams',
  //   headerName: 'Fixes: TKD Streams Miss',
  //   width: 120,
  //   sortable: true,
  //   hide: true,
  //   valueFormatter: zeroFormatter,
  // },
  // {
  //   field: 'completeDboms',
  //   headerName: 'DBoM Complete',
  //   width: 90,
  //   sortable: false,
  //   hide: true,
  //   valueFormatter: listFormatter,
  // },
  // {
  //   field: 'latestProductTs',
  //   headerName: 'Update Date',
  //   width: 120,
  //   sortable: true,
  //   hide: true,
  //   valueFormatter: dateFormatter,
  // },
];
